import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Input, Select, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useMemo, useState } from "react";
import { SAVE_CUSTOMER_REPORT_DATA } from "./graphql";

const CustomerReportSettingsForm = ({ id, originalReportData, hide }) => {
  const reportData = useMemo(() => {
    if (!originalReportData) return {};
    return JSON.parse(originalReportData);
  }, [originalReportData]);

  function sortBudgetsByYear(budgets) {
    return [...budgets].sort((a, b) => Number(a.year) - Number(b.year));
  }

  const [goals, setGoals] = useState(reportData.goals || "");
  const [salesBudgets, setSalesBudgets] = useState(
    sortBudgetsByYear(reportData.salesBudgets || []),
  );

  const [save, saveRes] = useMutation(SAVE_CUSTOMER_REPORT_DATA, {
    onCompleted() {
      Alert("success", "Customer report settings saved.");
      hide();
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const yearOptions = Array.from(
    { length: new Date().getFullYear() + 5 - 2024 + 1 },
    (_, i) => 2024 + i,
  );

  function saveHandler() {
    const yearSet = new Set();
    for (const budget of salesBudgets) {
      const year = Number(budget.year);
      if (yearSet.has(year)) {
        Alert("error", "Duplicate years are not allowed.");
        return;
      }
      yearSet.add(year);
    }

    const sortedSalesBudgets = sortBudgetsByYear(salesBudgets);
    save({
      variables: {
        id,
        reportData: JSON.stringify({
          ...reportData,
          goals,
          salesBudgets: sortedSalesBudgets,
        }),
      },
    });
  }

  function addBudgetHandler() {
    setSalesBudgets((prev) => {
      const sorted = sortBudgetsByYear(prev);
      const nextYear =
        sorted.length > 0
          ? Number(sorted[sorted.length - 1].year) + 1
          : new Date().getFullYear();

      return sortBudgetsByYear([
        ...sorted,
        { year: nextYear, amount: 0, numberOfStores: 0 },
      ]);
    });
  }

  function deleteBudgetHandler(index) {
    setSalesBudgets((prev) =>
      sortBudgetsByYear(prev.filter((_, i) => i !== index)),
    );
  }

  function updateBudgetYear(index, value) {
    const nextYear = Number(value);
    setSalesBudgets((prev) =>
      sortBudgetsByYear(
        prev.map((budget, i) =>
          i === index ? { ...budget, year: nextYear } : budget,
        ),
      ),
    );
  }

  function updateBudgetAmount(index, value) {
    const parsed = value === "" ? 0 : Number(value);
    const nextAmount = Number.isFinite(parsed) ? parsed : 0;
    setSalesBudgets((prev) =>
      sortBudgetsByYear(
        prev.map((budget, i) =>
          i === index ? { ...budget, amount: nextAmount } : budget,
        ),
      ),
    );
  }

  function updateBudgetStores(index, value) {
    const parsed = value === "" ? 0 : Number(value);
    const nextStores = Number.isFinite(parsed) ? parsed : 0;
    setSalesBudgets((prev) =>
      sortBudgetsByYear(
        prev.map((budget, i) =>
          i === index ? { ...budget, numberOfStores: nextStores } : budget,
        ),
      ),
    );
  }

  return (
    <div className="space-y-4 text-sm">
      <div>
        <label className="block mb-1">Target & Goals</label>
        <Text value={goals} onChange={(e) => setGoals(e.target.value)} />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label>Customer Data by Year</label>
        </div>

        {salesBudgets.length === 0 ? (
          <div className="mt-2 text-gray-500">No customer data.</div>
        ) : (
          <div className="mt-2 -mx-2">
            <table className="with-line">
              <thead>
                <tr>
                  <th>Year</th>
                  <th className="text-right">Budget(USD)</th>
                  <th className="text-right">Number of Stores</th>
                </tr>
              </thead>
              <tbody>
                {salesBudgets.map((budget, index) => (
                  <tr key={`${budget.year}-${index}`}>
                    <td>
                      <div className="flex items-center space-x-2">
                        <Button
                          trash
                          onClick={() => deleteBudgetHandler(index)}
                        />
                        <Select
                          value={budget.year}
                          onChange={(e) =>
                            updateBudgetYear(index, e.target.value)
                          }
                        >
                          {yearOptions.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </td>
                    <td>
                      <Input
                        className="text-right w-full"
                        type="number"
                        value={budget.amount}
                        onChange={(e) =>
                          updateBudgetAmount(index, e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Input
                        className="text-right w-full"
                        type="number"
                        value={budget.numberOfStores ?? 0}
                        onChange={(e) =>
                          updateBudgetStores(index, e.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2">
          <Button onClick={addBudgetHandler}>+ Add Year</Button>
        </div>
      </div>

      <div className="pt-2">
        <Button primary onClick={saveHandler} loading={saveRes.loading}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default CustomerReportSettingsForm;
